import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./App.css";
import { applyTheme, getStoredTheme, saveTheme } from "./theme";
import LoginPage from "./components/LoginPage";
import PublicLanding from "./components/PublicLanding";
import UXModalProvider from "./components/UXModalProvider";
import { clearAuthSession } from "./sessionCleanup";
import { apiFetch } from './api';

// Le pagine vengono caricate on demand: giochi (Konva, Unity) e aree riservate
// non appesantiscono il bundle iniziale della landing.
// Componenti giochi
const Welcome = lazy(() => import("./components/Welcome"));
const TrovaNumeri = lazy(() => import("./components/TrovaNumeri"));
const NumeroMancante = lazy(() => import("./components/NumeroMancante"));
const LetteraDoppia = lazy(() => import("./components/LetteraDoppia"));
const ReazioneInibizione = lazy(() => import("./components/ReazioneInibizione"));
const TrovaBandiere = lazy(() => import("./components/TrovaBandiere"));
const Labirinto = lazy(() => import("./components/Labirinto"));
const TrainSimulator = lazy(() => import("./components/TrainSimulator"));
const Classificatore = lazy(() => import("./components/Classificatore"));
const DoppioCanale = lazy(() => import("./components/DoppioCanale"));
// Componenti pagine
const Dashboard = lazy(() => import("./components/Dashboard"));
const PatientProfile = lazy(() => import("./components/PatientProfile"));
const ScoresPage = lazy(() => import("./components/ScoresPage"));
const Statistics = lazy(() => import("./components/Statistics"));
const ForgotPasswordPage = lazy(() => import("./components/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./components/ResetPasswordPage"));
const SignupPage = lazy(() => import("./components/SignupPage"));
const RegisterPatientPage = lazy(() => import("./components/RegisterPatientPage"));
const PatientsList = lazy(() => import("./components/PatientsList"));
const AdminTrainersPage = lazy(() => import("./components/AdminTrainersPage"));
const CompaniesPage = lazy(() => import("./components/CompaniesPage"));
const TrainerDataDashboard = lazy(() => import("./components/TrainerDataDashboard"));
const GameConfigPanel = lazy(() => import("./components/GameConfigPanel"));
const ImprintPage = lazy(() => import("./components/ImprintPage"));
const PrivacyPage = lazy(() => import("./components/PrivacyPage"));
const TermsPage = lazy(() => import("./components/TermsPage"));
const PricingPage = lazy(() => import("./components/PricingPage"));
const CompanyDashboardPage = lazy(() => import("./components/CompanyDashboardPage"));

// Componente wrapper per giochi
function GamesWrapper({ currentGame, setCurrentGame }) {
  const renderGame = () => {
    switch (currentGame) {
      case "gioco1":
        return <TrovaNumeri onHome={() => setCurrentGame(null)} />;
      case "gioco2":
        return <NumeroMancante onHome={() => setCurrentGame(null)} />;
      case "gioco3":
        return <LetteraDoppia onHome={() => setCurrentGame(null)} />;
      case "gioco4":
        return <ReazioneInibizione onHome={() => setCurrentGame(null)} />;
      case "gioco5":
        return <TrovaBandiere onHome={() => setCurrentGame(null)} />;
      case "gioco6":
        return <Labirinto onHome={() => setCurrentGame(null)} />;
      case "gioco7":
        return <TrainSimulator onHome={() => setCurrentGame(null)} />;
      case "gioco8":
        return <Classificatore onHome={() => setCurrentGame(null)} />;
      case "gioco9":
        return <DoppioCanale onHome={() => setCurrentGame(null)} />;
      default:
        return <Welcome onStart={setCurrentGame} />;
    }
  };

  return <div>{renderGame()}</div>;
}

// Protected Route Component
function PageLoader() {
  return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--bg)', color: 'var(--text)' }}>Caricamento...</div>;
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const { exp } = jwtDecode(token);
      if (exp * 1000 < Date.now()) {
        clearAuthSession({ preserveTheme: true });
        setIsAuthenticated(false);
      } else {
        setIsAuthenticated(true);
      }
    } catch {
      clearAuthSession({ preserveTheme: true });
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const sendHeartbeat = () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      let activeGame = null;
      try {
        const storedActivity = JSON.parse(localStorage.getItem('activeGameActivity') || 'null');
        if (storedActivity?.name && storedActivity?.startedAt) {
          activeGame = storedActivity;
        }
      } catch {
        localStorage.removeItem('activeGameActivity');
      }

      apiFetch('/api/presence/heartbeat', {
        method: 'POST',
        body: { activeGame }
      }).catch(() => {});
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 60_000);
    window.addEventListener('focus', sendHeartbeat);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', sendHeartbeat);
    };
  }, [isAuthenticated]);

  if (isAuthenticated === null) {
    return <PageLoader />;
  }

  return isAuthenticated ? (
    <>
      {children}
    </>
  ) : <Navigate to="/login" replace />;
}

export default function App() {
  const [currentGame, setCurrentGame] = useState(null);
  const [themeMode, setThemeMode] = useState(() => getStoredTheme());

  useEffect(() => {
    applyTheme(themeMode);
    saveTheme(themeMode);
  }, [themeMode]);

  // Load theme from database on app startup if user is logged in
  useEffect(() => {
    const loadThemeFromDatabase = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const res = await apiFetch('/api/user/theme');

        if (res.ok) {
          const data = await res.json();
          if (data.theme && data.theme !== themeMode) {
            setThemeMode(data.theme);
          }
        }
      } catch (err) {
        console.log('Could not load theme from database:', err);
      }
    };

    loadThemeFromDatabase();
  }, []); // Run once on component mount

  return (
    <UXModalProvider>
      <Router>
        <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Login, Signup e Recovery (pubblico) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/company/login" element={<LoginPage />} />
        <Route path="/register" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        {/* Dashboard e profilo */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard themeMode={themeMode} onThemeChange={setThemeMode} />
            </ProtectedRoute>
          }
        />
        <Route path="/company/dashboard" element={<ProtectedRoute><CompanyDashboardPage /></ProtectedRoute>} />
        <Route path="/trainer/data" element={<ProtectedRoute><TrainerDataDashboard /></ProtectedRoute>} />
        <Route path="/scores" element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
        <Route path="/games/user/scores.html" element={<ProtectedRoute><ScoresPage /></ProtectedRoute>} />

        {/* Pazienti */}
        <Route path="/patients" element={<ProtectedRoute><PatientsList /></ProtectedRoute>} />
        <Route path="/app/register/patients" element={<ProtectedRoute><PatientsList /></ProtectedRoute>} />
        <Route path="/admin/trainers" element={<ProtectedRoute><AdminTrainersPage /></ProtectedRoute>} />
        <Route path="/admin/companies" element={<ProtectedRoute><CompaniesPage /></ProtectedRoute>} />
        <Route path="/register/patient" element={<ProtectedRoute><RegisterPatientPage /></ProtectedRoute>} />
        <Route path="/app/register/patient" element={<ProtectedRoute><RegisterPatientPage /></ProtectedRoute>} />

        <Route path="/patient" element={<ProtectedRoute><PatientProfile /></ProtectedRoute>} />

        {/* Game Configuration */}
        <Route path="/game-config" element={<ProtectedRoute><GameConfigPanel /></ProtectedRoute>} />

        {/* Home (landing) */}
        <Route path="/" element={<PublicLanding />} />
        <Route path="/imprint" element={<ImprintPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/pricing" element={<PricingPage />} />

        {/* Giochi (protected) */}
        <Route path="/games" element={<ProtectedRoute><GamesWrapper currentGame={currentGame} setCurrentGame={setCurrentGame} /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </Router>
    </UXModalProvider>
  );
}